import { Box, CircularProgress } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Flex from "@/components/Flex";
import OutlineCardV2 from "@/components/OutlineCardV2";
import Text from "@/components/Text";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { NAVBAR_HEIGHT } from "@/components/app/nav-bar";
import { PROVIDER_PATHS } from "@/constants/paths";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import useDisclosure from "@/hooks/use-disclosure";
import { useQueryParams } from "@/hooks/use-query-param";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useGetSignatureByProviderID } from "@/queries/signature/use-get-signature-by-providerID";
import { getXtenantId } from "@/utils/utils";
import ViewEncounterSummaryDrawer from "@/pages/app/provider/billings/encounter/ViewEncounterSummaryDrawer";
import { VisitNoteConfirmationModal } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note";
import {
  GROUP_APPOINTMENT_STATUS,
  SESSION_ID_QUERY_PARAM,
} from "@/pages/app/provider/scheduling/group-appointment";
import AttendancePanel from "./components/AttendancePanel";
import FinalizeSessionModal from "./components/FinalizeSessionModal";
import GroupSessionNotePrintDrawer from "./components/GroupSessionNotePrintDrawer";
import IndividualNoteForm from "./components/IndividualNoteForm";
import SessionNoteBreadcrumb from "./components/SessionNoteBreadcrumb";
import SessionNoteHeader from "./components/SessionNoteHeader";
import SharedGroupNoteForm from "./components/SharedGroupNoteForm";
import {
  ATTENDANCE_PANEL_WIDTH,
  ATTENDANCE_TILE,
  ENCOUNTER_APPOINTMENT_QUERY_PARAM,
  ENCOUNTER_PATIENT_QUERY_PARAM,
  INDIVIDUAL_NOTE_UNSIGNED_CONFIRMATION,
  SESSION_NOTE_CANCEL_CONFIRMATION,
  SESSION_NOTE_LABEL,
  SESSION_NOTE_MESSAGE,
  SESSION_NOTE_STATE,
} from "./group-visit-constant";
import {
  getFinalizeChecks,
  getFinalizeSummary,
  toGroupSessionPrintData,
} from "./group-visit-utils";
import { useIndividualNote } from "./hooks/use-individual-note";
import { useSessionAttendance } from "./hooks/use-session-attendance";
import { useSessionDocumentation } from "./hooks/use-session-documentation";
import { useSessionLeaveGuard } from "./hooks/use-session-leave-guard";
import { useSharedGroupNote } from "./hooks/use-shared-group-note";
import { useSessionSignOff } from "./hooks/use-session-sign-off";
import { useSessionStatusActions } from "./hooks/use-session-status-actions";

/**
 * Group therapy session documentation — attendance beside the one note that
 * covers every attendee, and each attendee's own visit note.
 *
 * @author Rajkumar Rathod
 */
const GroupSessionNoteLayout = () => {
  const sessionId = useQueryParams(SESSION_ID_QUERY_PARAM) ?? undefined;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const cancelDisclosure = useDisclosure();
  const finalizeDisclosure = useDisclosure();
  const unsignedDisclosure = useDisclosure();
  // Carries the attendee's appointment uuid, which the summary is read from.
  const summaryDisclosure = useDisclosure();
  const printDisclosure = useDisclosure();
  const [selectedUuid, setSelectedUuid] = useState<string | undefined>();
  const [pendingUuid, setPendingUuid] = useState<string | undefined>();

  const documentation = useSessionDocumentation(sessionId);
  const sharedNote = useSharedGroupNote(sessionId);
  const attendance = useSessionAttendance(documentation.roster);
  // Saving an untouched note would create an empty one and mark it complete.
  const saveSharedNoteBeforeLeave = async () => {
    if (sharedNote.formik.dirty) await sharedNote.save();
  };
  // A signed-off session opens on the shared group note: its individual notes
  // are no longer selectable, only printable.
  const activeUuid = documentation.isLocked ? undefined : selectedUuid;
  const selectedIndex = attendance.attendees.findIndex(
    (participant) => participant.uuid === activeUuid
  );
  const selectedParticipant = attendance.attendees[selectedIndex];
  const individualNote = useIndividualNote(
    selectedParticipant,
    documentation.isLocked,
    documentation.header.therapyType
  );
  const isNoteReadOnly = documentation.isLocked || individualNote.isSignedOff;
  // Nothing to lose until the session loads, or while the open note is read-only.
  const leaveGuard = useSessionLeaveGuard(
    !!documentation.session && !isNoteReadOnly,
    saveSharedNoteBeforeLeave
  );
  const statusActions = useSessionStatusActions(
    sessionId,
    leaveGuard.navigateWithoutGuard
  );
  const sessionSignOff = useSessionSignOff(
    sessionId,
    leaveGuard.navigateWithoutGuard
  );

  // The session is signed off by its primary therapist, so the print footer
  // carries that provider's stored signature.
  const { data: signature } = useGetSignatureByProviderID({
    providerUuid:
      documentation.session?.primaryTherapistId ??
      documentation.session?.therapistId ??
      "",
    xTENANTID: getXtenantId(),
  });

  // The print reads the same two sources the screen does: the session and its
  // shared note.
  const printData = useMemo(
    () =>
      toGroupSessionPrintData(
        documentation.session,
        documentation.header,
        sharedNote.formik.values,
        (signature?.data as { providerSignature?: string } | undefined)
          ?.providerSignature
      ),
    [
      documentation.session,
      documentation.header,
      sharedNote.formik.values,
      signature,
    ]
  );

  const isSessionComplete =
    documentation.session?.sessionStatus === GROUP_APPOINTMENT_STATUS.COMPLETED;
  const groupNoteState =
    sharedNote.isComplete || isSessionComplete
      ? SESSION_NOTE_STATE.COMPLETE
      : SESSION_NOTE_STATE.DRAFT;

  // The chart drawers read the patient from the `:patientId` route param, so
  // it goes in the path; the appointment stays a query param.
  const selectParticipant = useCallback(
    (uuid?: string) => {
      setSelectedUuid(uuid);

      const next = attendance.attendees.find(
        (participant) => participant.uuid === uuid
      );
      if (next) {
        searchParams.set(ENCOUNTER_APPOINTMENT_QUERY_PARAM, next.uuid);
        searchParams.set(ENCOUNTER_PATIENT_QUERY_PARAM, next.patientUuid ?? "");
      } else {
        searchParams.delete(ENCOUNTER_APPOINTMENT_QUERY_PARAM);
        searchParams.delete(ENCOUNTER_PATIENT_QUERY_PARAM);
      }
      const pathname = next?.patientUuid
        ? `${PROVIDER_PATHS.GROUP_SESSION_NOTE}/${next.patientUuid}`
        : PROVIDER_PATHS.GROUP_SESSION_NOTE;
      // Switching attendee changes the path but stays on this screen.
      leaveGuard.navigateWithoutGuard(() =>
        navigate(
          { pathname, search: searchParams.toString() },
          { replace: true }
        )
      );
    },
    [
      attendance.attendees,
      searchParams,
      navigate,
      leaveGuard.navigateWithoutGuard,
    ]
  );

  const goToAttendee = (index: number) => {
    const next = attendance.attendees[index];
    if (!next) return;
    if (individualNote.isSignedOff) {
      selectParticipant(next.uuid);
      return;
    }
    setPendingUuid(next.uuid);
    unsignedDisclosure.open();
  };

  const handleLeaveUnsigned = () => {
    unsignedDisclosure.close();
    selectParticipant(pendingUuid);
    setPendingUuid(undefined);
  };

  const handleCancelSession = async () => {
    cancelDisclosure.close();
    await statusActions.cancelSession();
  };

  const handleFinalize = async () => {
    if (!sharedNote.isComplete) {
      showSnackbar({
        message: SESSION_NOTE_MESSAGE.finalizeBlocked,
        type: "error",
      });
      return;
    }
    // The sign-off signs the shared note and completes the session in one call.
    await sessionSignOff.signOff(sharedNote.formik.values.overallGroupSummary);
    finalizeDisclosure.close();
  };

  const handleMarkAllPresent = () => {
    attendance.markAllPresent();
    showSnackbar({
      message: SESSION_NOTE_MESSAGE.allMarkedPresent,
      type: "success",
    });
  };

  const handleUnmarkAll = () => {
    attendance.unmarkAll();
    showSnackbar({
      message: SESSION_NOTE_MESSAGE.allUnmarked,
      type: "success",
    });
  };

  const totalParticipants =
    attendance.tally.find((tile) => tile.id === ATTENDANCE_TILE.TOTAL)?.count ??
    0;
  const readiness = {
    totalParticipants,
    markedParticipants: attendance.tally
      .filter((tile) => tile.id !== ATTENDANCE_TILE.TOTAL)
      .reduce((sum, tile) => sum + tile.count, 0),
    attendeeCount: attendance.attendeeCount,
    isGroupNoteComplete: sharedNote.isComplete,
    completedNotes: attendance.completedNotes,
    requiredNotes: attendance.requiredNotes,
  };
  const hasIncompleteNotes =
    attendance.completedNotes < attendance.requiredNotes;
  // Every present attendee signed, and the shared note marked complete.
  const isDocumentationComplete =
    !hasIncompleteNotes && groupNoteState === SESSION_NOTE_STATE.COMPLETE;

  if (documentation.isNotFound) {
    return (
      <Flex justifyContent="center" alignItems="center" sx={{ p: 4 }}>
        <Text {...CHART_TYPOGRAPHY.body4} c={CHART_COLORS.textMutedGrey}>
          {SESSION_NOTE_MESSAGE.sessionNotFound}
        </Text>
      </Flex>
    );
  }

  return (
    <Box
      sx={{
        height: `calc(100svh - ${NAVBAR_HEIGHT})`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: CHART_COLORS.surfaceDefault,
      }}
    >
      <SessionNoteHeader
        groupNoteState={groupNoteState}
        canFinalize={!hasIncompleteNotes}
        isDocumentationComplete={isDocumentationComplete}
        isPending={!documentation.isLocked}
        isLocked={documentation.isLocked}
        onCancelSession={cancelDisclosure.open}
        onCompleteSession={finalizeDisclosure.open}
        onPrint={printDisclosure.open}
      />

      <SessionNoteBreadcrumb
        participant={selectedParticipant}
        isSaving={individualNote.isSaving}
        onOpenSharedNote={() => selectParticipant(undefined)}
      />

      <Flex gap={2} sx={{ flex: 1, minHeight: 0, p: 2 }}>
        <OutlineCardV2
          sx={{
            width: ATTENDANCE_PANEL_WIDTH,
            flexShrink: 0,
            bgcolor: CHART_COLORS.surfaceWhite,
            display: { xs: "none", lg: "flex" },
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <AttendancePanel
            participants={attendance.participants}
            tally={attendance.tally}
            searchKey={attendance.searchKey}
            onSearch={attendance.setSearchKey}
            onStatusChange={attendance.setStatus}
            onMarkAllPresent={handleMarkAllPresent}
            onUnmarkAll={handleUnmarkAll}
            isAllPresent={attendance.isAllPresent}
            completedNotes={attendance.completedNotes}
            requiredNotes={attendance.requiredNotes}
            selectedUuid={activeUuid}
            onSelect={selectParticipant}
            onShowSummary={
              documentation.isLocked ? summaryDisclosure.open : undefined
            }
            disabled={documentation.isLocked}
          />
        </OutlineCardV2>

        <OutlineCardV2
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: CHART_COLORS.surfaceWhite,
          }}
        >
          {documentation.isLoading ||
          sharedNote.isLoading ||
          individualNote.isLoading ? (
            <Flex justifyContent="center" alignItems="center" flex={1}>
              <CircularProgress size={28} />
            </Flex>
          ) : selectedParticipant ? (
            <IndividualNoteForm
              participant={selectedParticipant}
              formik={individualNote.formik}
              groupType={documentation.header.therapyType}
              template={individualNote.template}
              summary={individualNote.summary}
              onTemplateChange={individualNote.setTemplate}
              onMarkComplete={individualNote.save}
              onPrev={() => goToAttendee(selectedIndex - 1)}
              onNext={() => goToAttendee(selectedIndex + 1)}
              hasPrev={selectedIndex > 0}
              hasNext={selectedIndex < attendance.attendees.length - 1}
              disabled={isNoteReadOnly}
            />
          ) : (
            <>
              {/* minHeight lets the note scroll while the footer stays put. */}
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
                <SharedGroupNoteForm
                  formik={sharedNote.formik}
                  header={documentation.header}
                  attendeeCount={attendance.attendeeCount}
                  disabled={documentation.isLocked}
                />
              </Box>

              {!documentation.isLocked && (
                <Flex
                  justifyContent="flex-end"
                  sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 1.5,
                    borderTop: `1px solid ${CHART_COLORS.borderLight}`,
                  }}
                >
                  <StyledButtonV2
                    label={SESSION_NOTE_LABEL.markComplete}
                    variant="primary"
                    onClick={sharedNote.save}
                    sx={{
                      bgcolor: CHART_COLORS.badgeSuccessText,
                      "&:hover": { bgcolor: CHART_COLORS.badgeSuccessText },
                    }}
                  />
                </Flex>
              )}
            </>
          )}
        </OutlineCardV2>
      </Flex>

      {finalizeDisclosure.opened && (
        <FinalizeSessionModal
          open={finalizeDisclosure.opened}
          onClose={finalizeDisclosure.close}
          checks={getFinalizeChecks(readiness)}
          summary={getFinalizeSummary(readiness)}
          hasIncompleteNotes={hasIncompleteNotes}
          onConfirm={handleFinalize}
          isSubmitting={sessionSignOff.isSubmitting}
        />
      )}

      {/* Mounted per open so the print never starts from the last patient's note. */}
      {summaryDisclosure.opened && (
        <ViewEncounterSummaryDrawer
          key={summaryDisclosure.data}
          disclosure={summaryDisclosure}
          hideChiefComplaint
        />
      )}

      <GroupSessionNotePrintDrawer
        disclosure={printDisclosure}
        data={printData}
      />

      <ConfirmationModal
        disclosureHost={cancelDisclosure}
        title={SESSION_NOTE_CANCEL_CONFIRMATION.title}
        content={SESSION_NOTE_CANCEL_CONFIRMATION.content}
        confirmationText={SESSION_NOTE_CANCEL_CONFIRMATION.confirm}
        cancelText={SESSION_NOTE_CANCEL_CONFIRMATION.keep}
        onConfirm={handleCancelSession}
      />

      <VisitNoteConfirmationModal
        disclosureHost={leaveGuard.leaveDisclosure}
        onConfirm={leaveGuard.confirmLeave}
      />

      <ConfirmationModal
        disclosureHost={unsignedDisclosure}
        title={INDIVIDUAL_NOTE_UNSIGNED_CONFIRMATION.title}
        content={INDIVIDUAL_NOTE_UNSIGNED_CONFIRMATION.content}
        confirmationText={INDIVIDUAL_NOTE_UNSIGNED_CONFIRMATION.confirm}
        cancelText={INDIVIDUAL_NOTE_UNSIGNED_CONFIRMATION.cancel}
        onConfirm={handleLeaveUnsigned}
      />
    </Box>
  );
};

export default GroupSessionNoteLayout;
